import React, { useEffect, useMemo, useState } from "react";
import {
    Clock, Trash2, PlayCircle, Save, History, Layers,
    AlertTriangle, ShieldCheck, FileText, Bell, KeyRound, Database, Sparkles,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Retention — tenant-level multi-class retention policy.
 *
 * Backend (`/api/settings/retention`) exposes one policy blob covering 5 data
 * classes (file_versions, audit_logs, alerts, login_attempts,
 * completed_migrations). Each class has its own enable / keep_last_n /
 * max_age_days knobs. `class_meta` from the API drives the UI so adding a new
 * class on the backend only requires registering an icon below.
 */

const ICONS = {
    file_versions: Layers,
    audit_logs: FileText,
    alerts: Bell,
    login_attempts: KeyRound,
    completed_migrations: Database,
};

const TINTS = {
    file_versions: "#1D4ED8",
    audit_logs: "#00C2FF",
    alerts: "#f59e0b",
    login_attempts: "#6b7280",
    completed_migrations: "#22c55e",
};

export default function Retention({ fullWidth = false }) {
    const { user } = useAuth();
    const canEdit = user?.role === "admin" || user?.role === "superadmin";

    const [policy, setPolicy] = useState(null);
    const [meta, setMeta] = useState(null);
    const [totals, setTotals] = useState({});
    const [draft, setDraft] = useState({});           // { class_key: {enabled, keep_last_n, max_age_days} }
    const [preview, setPreview] = useState(null);     // { would_delete: {class_key: int} }
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await http.get("/settings/retention");
            setPolicy(data.policy);
            setMeta(data.class_meta);
            setTotals(data.totals || {});
            // Seed the draft from the persisted policy so edits are local until Save.
            // We MUST NOT substitute class_meta defaults here, otherwise dirty-detection
            // would flag the initial unedited draft as dirty (persisted policy stores
            // null until the operator picks a value).
            const seed = {};
            for (const k of Object.keys(data.class_meta || {})) {
                const p = data.policy?.[k] || {};
                seed[k] = {
                    enabled: !!p.enabled,
                    keep_last_n: p.keep_last_n ?? null,
                    max_age_days: p.max_age_days ?? null,
                };
            }
            setDraft(seed);
        } catch (e) {
            toast.error(errMsg(e));
        }
    };
    useEffect(() => { load(); }, []);

    const dirty = useMemo(() => {
        if (!policy) return false;
        for (const k of Object.keys(draft)) {
            const p = policy[k] || {};
            const d = draft[k] || {};
            if (!!p.enabled !== !!d.enabled) return true;
            if ((p.keep_last_n ?? null) !== (d.keep_last_n ?? null)) return true;
            if ((p.max_age_days ?? null) !== (d.max_age_days ?? null)) return true;
        }
        return false;
    }, [policy, draft]);

    const anyEnabled = useMemo(
        () => Object.values(draft).some((d) => d?.enabled),
        [draft],
    );

    const updateClass = (key, patch) => {
        setDraft((prev) => {
            const cur = prev[key] || {};
            const next = { ...cur, ...patch };
            // First time the operator flips a class ON, prefill the rule inputs
            // with the catalog defaults so they aren't staring at empty boxes.
            // Subsequent toggles preserve whatever the user typed.
            if (patch.enabled === true && !cur.enabled) {
                const m = meta?.[key] || {};
                if (next.keep_last_n == null && m.supports_keep_n) next.keep_last_n = m.default_keep_n ?? null;
                if (next.max_age_days == null) next.max_age_days = m.default_max_age ?? null;
            }
            return { ...prev, [key]: next };
        });
    };

    const save = async () => {
        // Reject saves where a class is enabled with no rule set — backend will
        // reject too but we want a fast UI hint.
        for (const [k, d] of Object.entries(draft)) {
            if (d.enabled && !d.keep_last_n && !d.max_age_days) {
                toast.error(`${meta[k]?.label || k}: enable at least one of "keep last" or "max age".`);
                return;
            }
        }
        setBusy(true);
        try {
            const body = {};
            for (const [k, d] of Object.entries(draft)) {
                body[k] = {
                    enabled: !!d.enabled,
                    keep_last_n: meta[k]?.supports_keep_n ? (d.keep_last_n ? Number(d.keep_last_n) : null) : null,
                    max_age_days: d.max_age_days ? Number(d.max_age_days) : null,
                };
            }
            await http.put("/settings/retention", body);
            toast.success("Retention policy saved");
            setPreview(null);
            await load();
        } catch (e) {
            toast.error(errMsg(e));
        } finally { setBusy(false); }
    };

    const doPreview = async () => {
        setBusy(true);
        try {
            const { data } = await http.post("/settings/retention/preview");
            setPreview(data);
        } catch (e) {
            toast.error(errMsg(e));
        } finally { setBusy(false); }
    };

    const doRun = async () => {
        const total = preview
            ? Object.values(preview.would_delete || {}).reduce((a, b) => a + b, 0)
            : null;
        const confirmMsg = total != null
            ? `Permanently delete ~${total.toLocaleString()} record${total === 1 ? "" : "s"} matched by the active classes? This cannot be undone.`
            : "Run retention sweep across every enabled class? This permanently deletes matching records.";
        if (!window.confirm(confirmMsg)) return;
        setBusy(true);
        try {
            const { data } = await http.post("/settings/retention/run");
            toast.success(`Deleted ${data.total_deleted.toLocaleString()} record${data.total_deleted === 1 ? "" : "s"} across ${Object.keys(data.summary || {}).filter(k => data.summary[k] > 0).length} class${Object.keys(data.summary || {}).filter(k => data.summary[k] > 0).length === 1 ? "" : "es"}.`);
            setPreview(null);
            await load();
        } catch (e) {
            toast.error(errMsg(e));
        } finally { setBusy(false); }
    };

    const doApplyDefaults = async () => {
        if (!window.confirm(
            "Apply SOC2-friendly retention defaults?\n\n"
            + "• File versions — keep last 30, expire after 90 days\n"
            + "• Audit logs — 365 days (compliance window)\n"
            + "• Resolved alerts — 90 days\n"
            + "• Login attempts — 14 days\n"
            + "• Completed migrations — 180 days\n\n"
            + "This overwrites every class. Save afterwards if you want to tune individual rules."
        )) return;
        setBusy(true);
        try {
            await http.post("/settings/retention/apply-defaults");
            toast.success("SOC2 defaults applied across all 5 classes.");
            setPreview(null);
            await load();
        } catch (e) {
            toast.error(errMsg(e));
        } finally { setBusy(false); }
    };

    if (!policy || !meta) {
        return <div className="p-8 text-sm text-muted-foreground" data-testid="retention-loading">Loading retention policy…</div>;
    }

    const lastRun = policy.last_run_at ? new Date(policy.last_run_at).toLocaleString() : "never";
    const lastSummary = policy.last_run_summary || {};
    const lastTotal = typeof lastSummary === "object"
        ? Object.values(lastSummary).reduce((a, b) => (typeof b === "number" ? a + b : a), 0)
        : 0;
    const grandTotal = Object.values(totals).reduce((a, b) => a + (b || 0), 0);

    return (
        <div className={`mx-auto space-y-5 p-6 ${fullWidth ? "max-w-none" : "max-w-5xl"}`} data-testid="retention-page">
            <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                    Data lifecycle
                </div>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="mt-1 font-display text-2xl font-bold">Retention policies</h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Cap storage cost and meet compliance windows by automatically expiring stale data across {Object.keys(meta).length} classes.
                            A nightly sweep runs across every tenant — you can also dry-run + apply manually below.
                        </p>
                    </div>
                    {canEdit && (
                        <button
                            type="button"
                            onClick={doApplyDefaults}
                            disabled={busy}
                            data-testid="retention-apply-defaults-btn"
                            className="inline-flex items-center gap-1.5 rounded-md border border-[#1D4ED8]/50 bg-[#1D4ED8]/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-[#1D4ED8] hover:bg-[#1D4ED8]/15 disabled:opacity-60"
                            title="Overwrite every class with SOC2-friendly defaults"
                        >
                            <Sparkles size={13} /> Apply SOC2 defaults
                        </button>
                    )}
                </div>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatCard icon={Layers} label="Total records" value={grandTotal.toLocaleString()} tint="#1D4ED8" />
                <StatCard
                    icon={ShieldCheck}
                    label="Active classes"
                    value={`${Object.values(policy).filter((p) => p && typeof p === "object" && p.enabled).length} / ${Object.keys(meta).length}`}
                    tint="#22c55e"
                />
                <StatCard icon={Clock} label="Last run" value={lastRun} tint="#00C2FF" small />
                <StatCard icon={Trash2} label="Last deleted" value={lastTotal.toLocaleString()} tint="#f59e0b" />
            </div>

            {/* Per-class cards */}
            <div className="space-y-3" data-testid="retention-classes">
                {Object.entries(meta).map(([key, m]) => (
                    <ClassCard
                        key={key}
                        classKey={key}
                        meta={m}
                        draft={draft[key] || {}}
                        total={totals[key] || 0}
                        lastDeleted={lastSummary?.[key] ?? null}
                        wouldDelete={preview?.would_delete?.[key] ?? null}
                        canEdit={canEdit}
                        onChange={(patch) => updateClass(key, patch)}
                    />
                ))}
            </div>

            {/* Footer action bar */}
            <div className="sticky bottom-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface/95 p-3 shadow-lg backdrop-blur">
                <div className="text-[11px] text-muted-foreground">
                    {dirty
                        ? <span className="text-[hsl(var(--warning))]">Unsaved changes — preview reflects the saved policy.</span>
                        : <span>Policy in sync with server.</span>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={doPreview}
                        disabled={busy || dirty || !anyEnabled}
                        title={dirty ? "Save your changes to preview" : !anyEnabled ? "Enable at least one class" : "Preview deletions"}
                        data-testid="retention-preview-btn"
                        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest hover:border-foreground/40 disabled:opacity-50"
                    >
                        <History size={13} /> Preview
                    </button>
                    {canEdit && (
                        <button
                            type="button"
                            onClick={doRun}
                            disabled={busy || !anyEnabled || dirty}
                            title={dirty ? "Save your changes first" : !anyEnabled ? "Enable at least one class" : "Run sweep now"}
                            data-testid="retention-run-btn"
                            className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/15 disabled:opacity-50"
                        >
                            <PlayCircle size={13} /> Run now
                        </button>
                    )}
                    {canEdit && (
                        <button
                            type="button"
                            onClick={save}
                            disabled={busy || !dirty}
                            data-testid="retention-save-btn"
                            className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#1D4ED8] to-[#00C2FF] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white shadow-md disabled:opacity-60"
                        >
                            <Save size={13} /> Save policy
                        </button>
                    )}
                </div>
            </div>

            {/* Preview totals */}
            {preview && (
                <PreviewBanner preview={preview} meta={meta} onDismiss={() => setPreview(null)} />
            )}

            {!canEdit && (
                <div className="rounded-md border border-border bg-background/60 p-3 text-[11px] text-muted-foreground" data-testid="retention-readonly">
                    Read-only. Only admins can change or enforce the retention policy.
                </div>
            )}
        </div>
    );
}

// ---- per-class card ------------------------------------------------------

function ClassCard({ classKey, meta, draft, total, lastDeleted, wouldDelete, canEdit, onChange }) {
    const Icon = ICONS[classKey] || Layers;
    const tint = TINTS[classKey] || "#1D4ED8";
    const enabled = !!draft.enabled;

    return (
        <div
            className={[
                "rounded-xl border bg-surface p-4 transition-colors",
                enabled ? "border-border" : "border-border/60 bg-muted/10",
            ].join(" ")}
            data-testid={`retention-class-${classKey}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                    <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                        style={{ background: `${tint}22`, color: tint }}
                    >
                        <Icon size={16} />
                    </div>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="font-display text-base font-semibold">{meta.label}</div>
                            <span
                                className="rounded-full border border-border bg-background/50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                                data-testid={`retention-${classKey}-total`}
                            >
                                {total.toLocaleString()} stored
                            </span>
                            {wouldDelete != null && wouldDelete > 0 && (
                                <span
                                    className="rounded-full border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--destructive))]"
                                    data-testid={`retention-${classKey}-preview`}
                                >
                                    ~{wouldDelete.toLocaleString()} would be deleted
                                </span>
                            )}
                            {lastDeleted != null && lastDeleted > 0 && (
                                <span
                                    className="rounded-full border border-border bg-background/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                                    title="Removed by last sweep"
                                >
                                    Last sweep: {lastDeleted.toLocaleString()}
                                </span>
                            )}
                        </div>
                        <div className="mt-1 text-[12px] text-muted-foreground">{meta.desc}</div>
                    </div>
                </div>
                <Switch
                    checked={enabled}
                    disabled={!canEdit}
                    onChange={(v) => onChange({ enabled: v })}
                    testId={`retention-${classKey}-enabled`}
                />
            </div>

            {enabled && (
                <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-border pt-3">
                    {meta.supports_keep_n && (
                        <RuleField
                            label="Keep last"
                            suffix="versions"
                            value={draft.keep_last_n ?? ""}
                            disabled={!canEdit}
                            min={1} max={10000}
                            testId={`retention-${classKey}-keep-n`}
                            onChange={(v) => onChange({ keep_last_n: v })}
                        />
                    )}
                    <RuleField
                        label="Delete older than"
                        suffix="days"
                        value={draft.max_age_days ?? ""}
                        disabled={!canEdit}
                        min={1} max={3650}
                        testId={`retention-${classKey}-max-age`}
                        onChange={(v) => onChange({ max_age_days: v })}
                    />
                    {!draft.keep_last_n && !draft.max_age_days && (
                        <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--warning))]" data-testid={`retention-${classKey}-empty`}>
                            <AlertTriangle size={12} /> Set at least one rule to activate.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ---- preview banner ------------------------------------------------------

function PreviewBanner({ preview, meta, onDismiss }) {
    const entries = Object.entries(preview.would_delete || {})
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1]);
    const total = Object.values(preview.would_delete || {}).reduce((a, b) => a + b, 0);

    return (
        <div
            className="rounded-xl border border-[#1D4ED8]/40 bg-gradient-to-br from-[#1D4ED8]/5 to-[#00C2FF]/5 p-5"
            data-testid="retention-preview-result"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                        Dry run
                    </div>
                    <div className="mt-1 font-display text-3xl font-bold tabular-nums">
                        {total.toLocaleString()}
                        <span className="ml-2 text-sm font-medium text-muted-foreground">
                            record{total === 1 ? "" : "s"} would be removed
                        </span>
                    </div>
                    {entries.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {entries.map(([k, v]) => (
                                <div
                                    key={k}
                                    className="rounded-md border border-border bg-background/50 px-2 py-1 font-mono text-[11px]"
                                    data-testid={`retention-preview-${k}`}
                                >
                                    <span className="text-muted-foreground">{meta[k]?.label || k}</span>
                                    <span className="ml-1.5 font-semibold tabular-nums">{v.toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {entries.length === 0 && (
                        <div className="mt-2 text-[11px] text-muted-foreground">
                            Nothing matches the current policy — your data is already within retention windows.
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="rounded-md border border-border p-1 text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                    aria-label="Dismiss preview"
                    data-testid="retention-preview-dismiss"
                >
                    ×
                </button>
            </div>
        </div>
    );
}

// ---- tiny primitives -----------------------------------------------------

function StatCard({ icon: Icon, label, value, tint, small }) {
    return (
        <div className="rounded-lg border border-border bg-surface p-3">
            <div className="flex items-center gap-2">
                <div
                    className="flex h-7 w-7 items-center justify-center rounded-md"
                    style={{ background: `${tint}22`, color: tint }}
                >
                    <Icon size={12} />
                </div>
                <div className="font-mono text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {label}
                </div>
            </div>
            <div className={"mt-1.5 font-display font-bold tabular-nums " + (small ? "text-sm" : "text-xl")}>
                {value}
            </div>
        </div>
    );
}

function Switch({ checked, onChange, disabled, testId }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => onChange(!checked)}
            data-testid={testId}
            className={[
                "relative h-6 w-11 shrink-0 rounded-full transition-colors",
                checked ? "bg-[#1D4ED8]" : "bg-muted",
                disabled ? "cursor-not-allowed opacity-60" : "",
            ].join(" ")}
        >
            <span
                className={[
                    "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all",
                    checked ? "left-[22px]" : "left-0.5",
                ].join(" ")}
            />
        </button>
    );
}

function RuleField({ label, suffix, value, onChange, min, max, disabled, testId }) {
    return (
        <label className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">{label}</span>
            <input
                type="number"
                value={value === null || value === undefined ? "" : value}
                min={min}
                max={max}
                disabled={disabled}
                onChange={(e) => {
                    const v = e.target.value;
                    onChange(v === "" ? null : Number(v));
                }}
                data-testid={testId}
                className="mb-focus w-24 rounded-md border border-input bg-background px-2 py-1.5 text-right font-mono text-sm disabled:opacity-50"
            />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {suffix}
            </span>
        </label>
    );
}
